/**
 * Speech helper: recognition via the FunASR realtime service, synthesis via the Web Speech API
 * with the MiMo TTS backend (MimoTts.php) as a fallback when the built-in engine is unavailable.
 */
import FunASR from '@/utils/funasr-sdk';
import { ASR_URL, ASR_TOKEN } from '@/utils/asrConfig';
import { showToast } from '@/utils/toast';
import { normalizeRecorderError } from '@/utils/voiceRecorder';
import { TTS_API_URL } from '@/api/config';

// Below MIMO_TTS_MAX_CHARS in backend/MimoTts.php (600), short enough for a quick first clip.
const REMOTE_TTS_MAX_CHARS = 300;
// How long the built-in engine may take to start before falling back to the remote service.
const NATIVE_START_TIMEOUT_MS = 3000;
// MimoTts.php streams raw PCM16 mono at this rate (also sent as X-Audio-Sample-Rate).
const REMOTE_TTS_SAMPLE_RATE = 24000;
// Small lead before the first streamed piece (and after an underrun) so it does not start clipped.
const REMOTE_TTS_START_LEAD_S = 0.08;

const getSpeechLanguage = () => {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const language = (params.get('language') || '').toLowerCase();
    return language.startsWith('en') ? 'en-US' : 'zh-CN';
  } catch (error) {
    return 'zh-CN';
  }
};

const splitTextForSpeech = (text, maxLength = 180) => {
  const normalizedText = String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)]\([^)]*\)/g, '$1')
    .replace(/[#*_~>|-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalizedText) return [];

  const chunks = [];
  let rest = normalizedText;
  while (rest.length > maxLength) {
    const slice = rest.slice(0, maxLength);
    const splitAt = Math.max(
      slice.lastIndexOf('。'),
      slice.lastIndexOf('！'),
      slice.lastIndexOf('？'),
      slice.lastIndexOf('.'),
      slice.lastIndexOf('!'),
      slice.lastIndexOf('?'),
      slice.lastIndexOf('，'),
      slice.lastIndexOf(','),
      slice.lastIndexOf(' ')
    );
    const end = splitAt > 40 ? splitAt + 1 : maxLength;
    chunks.push(rest.slice(0, end).trim());
    rest = rest.slice(end).trim();
  }
  if (rest) chunks.push(rest);
  return chunks;
};

export class SpeechService {
  constructor() {
    this.synth = window.speechSynthesis;
    this.currentUtterance = null;
    this.speechQueue = [];
    this.speechCallbacks = {};
    this.isStoppingSpeech = false;
    this.isSpeaking = false;
    // Bumped on every speak/stop so callbacks from an older run are ignored.
    this.speechSession = 0;
    // Set once the built-in engine fails; later calls go straight to the remote service.
    this.nativeSynthesisBroken = false;
    this.audioContext = null;
    this.remoteControllers = [];
    this.remoteReader = null;
    this.remoteSources = new Set();
    // Context time at which the last scheduled PCM piece finishes.
    this.remotePlayhead = 0;
    this.remotePaused = false;

    this.recognition = null;
    this.isListening = false;
  }

  isRecognitionSupported() {
    return FunASR.Recognizer.isSupported();
  }

  isSupported() {
    return this.isRecognitionSupported();
  }

  // The remote TTS service covers browsers without a usable speechSynthesis.
  isSynthesisSupported() {
    return this.isNativeSynthesisUsable() || (typeof Audio !== 'undefined' && typeof fetch !== 'undefined');
  }

  isNativeSynthesisUsable() {
    return !this.nativeSynthesisBroken && !!this.synth && typeof SpeechSynthesisUtterance !== 'undefined';
  }

  /**
   * Stream the microphone to the ASR service.
   * onResult(text, isFinal): partial text of the current sentence while speaking,
   * then the corrected, punctuated sentence with isFinal = true.
   */
  startListening({ onResult, onError, onEnd } = {}) {
    if (!this.isRecognitionSupported()) {
      if (onError) onError(new Error('浏览器不支持语音识别，请使用 HTTPS 或 localhost 访问'));
      return false;
    }

    if (this.isListening) {
      this.stopListening();
    }

    const recognition = new FunASR.Recognizer({
      url: ASR_URL,
      token: ASR_TOKEN,
      onPartial: (text) => {
        if (text && onResult) onResult(text, false);
      },
      onFinal: (sentence) => {
        if (sentence.text && onResult) onResult(sentence.text, true);
      },
      onError: (error) => {
        console.error('Speech recognition error', error);
        if (onError) onError(error);
      },
      onStateChange: (state) => {
        if ((state === 'idle' || state === 'error') && this.recognition === recognition) {
          this.recognition = null;
          this.isListening = false;
          if (onEnd) onEnd();
        }
      }
    });

    this.recognition = recognition;
    this.isListening = true;
    recognition.start().catch(() => {
      // Reported through onError / onStateChange.
    });
    return true;
  }

  stopListening() {
    if (this.recognition && this.isListening) {
      // Resolves after the last sentence arrives; onEnd fires from onStateChange.
      this.recognition.stop();
    }
  }

  speak(text, { onEnd, onError, lang = getSpeechLanguage(), rate = 1, pitch = 1 } = {}) {
    this.stopSpeaking();
    this.isStoppingSpeech = false;

    const useNative = this.isNativeSynthesisUsable();
    const chunks = splitTextForSpeech(text, useNative ? 180 : REMOTE_TTS_MAX_CHARS);
    if (!chunks.length) {
      if (onEnd) onEnd();
      return false;
    }

    this.speechQueue = chunks;
    this.speechCallbacks = { onEnd, onError, lang, rate, pitch };
    // Unlock the audio element while still inside the click handler, so remote
    // clips are allowed to play later, including after a mid-way fallback.
    this.unlockRemoteAudio();
    if (useNative) {
      this.speakNextChunk();
    } else {
      this.startRemoteSpeech();
    }
    return true;
  }

  finishSpeech() {
    this.isSpeaking = false;
    this.currentUtterance = null;
    const onEnd = this.speechCallbacks.onEnd;
    this.speechCallbacks = {};
    if (onEnd) onEnd();
  }

  failSpeech(error) {
    const onError = this.speechCallbacks.onError;
    this.stopSpeaking();
    if (onError) onError(error);
  }

  speakNextChunk() {
    if (!this.synth || !this.speechQueue.length) {
      this.finishSpeech();
      return;
    }

    const session = this.speechSession;
    const { lang, rate, pitch } = this.speechCallbacks;
    const chunkText = this.speechQueue.shift();
    const utterance = new SpeechSynthesisUtterance(chunkText);
    utterance.lang = lang;
    utterance.rate = rate;
    utterance.pitch = pitch;

    // Some WebViews expose speechSynthesis but never speak (no voices, no events).
    // If nothing starts in time, hand this chunk and the rest to the remote service.
    let started = false;
    const watchdog = window.setTimeout(() => {
      if (started || session !== this.speechSession || this.currentUtterance !== utterance) return;
      this.switchToRemote(chunkText, 'native speech did not start');
    }, NATIVE_START_TIMEOUT_MS);

    utterance.onstart = () => {
      started = true;
      window.clearTimeout(watchdog);
    };

    utterance.onend = () => {
      window.clearTimeout(watchdog);
      if (session !== this.speechSession || this.currentUtterance !== utterance) return;
      this.currentUtterance = null;
      this.speakNextChunk();
    };

    utterance.onerror = (event) => {
      window.clearTimeout(watchdog);
      if (this.isStoppingSpeech || session !== this.speechSession) return;
      const reason = event && event.error;
      if (reason === 'interrupted' || reason === 'canceled') return;
      this.switchToRemote(chunkText, reason);
    };

    this.currentUtterance = utterance;
    this.isSpeaking = true;
    this.synth.resume();
    this.synth.speak(utterance);
  }

  /** The built-in engine failed: remember it and continue the same text with the remote service. */
  switchToRemote(pendingText, reason) {
    console.warn('Speech synthesis unavailable, falling back to remote TTS:', reason);
    this.nativeSynthesisBroken = true;
    this.currentUtterance = null;
    this.isStoppingSpeech = true;
    try {
      if (this.synth) this.synth.cancel();
    } catch (error) {
      // The engine is already unusable.
    }
    setTimeout(() => {
      this.isStoppingSpeech = false;
    }, 0);

    const rest = [pendingText, ...this.speechQueue].join(' ');
    this.speechQueue = splitTextForSpeech(rest, REMOTE_TTS_MAX_CHARS);
    this.startRemoteSpeech();
  }

  // ----- Remote synthesis (MiMo TTS via backend/MimoTts.php) -----
  // The backend streams raw 24 kHz PCM16 mono. Each network chunk is scheduled on a
  // Web Audio timeline as soon as it arrives, so playback starts after the first chunk.

  getAudioContext() {
    if (!this.audioContext) {
      const AudioContextClass = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
      if (!AudioContextClass) return null;
      this.audioContext = new AudioContextClass();
    }
    return this.audioContext;
  }

  // Called inside the click handler: browsers only let a user gesture start an AudioContext.
  unlockRemoteAudio() {
    const context = this.getAudioContext();
    if (!context) return;
    try {
      if (context.state === 'suspended') {
        const resuming = context.resume();
        if (resuming && resuming.catch) resuming.catch(() => {});
      }
      // A one-sample silent buffer completes the unlock on older iOS WebKit.
      const source = context.createBufferSource();
      source.buffer = context.createBuffer(1, 1, 22050);
      source.connect(context.destination);
      source.start(0);
    } catch (error) {
      // Playback reports the failure later if audio stays locked.
    }
  }

  /** Open the streaming request for one chunk. Resolves with the response once headers arrive. */
  openRemoteStream(text) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    if (controller) this.remoteControllers.push(controller);

    const request = fetch(TTS_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, lang: this.speechCallbacks.lang }),
      signal: controller ? controller.signal : undefined
    }).then(async (response) => {
      const type = response.headers.get('Content-Type') || '';
      if (!response.ok || !type.startsWith('audio/')) {
        let message = `HTTP ${response.status}`;
        try {
          const data = await response.json();
          if (data && data.message) message = data.message;
        } catch (error) {
          // Keep the status message.
        }
        throw new Error(message);
      }
      return response;
    });
    // A prefetched chunk may never be awaited if playback stops first.
    request.catch(() => {});
    return request;
  }

  startRemoteSpeech() {
    if (!this.getAudioContext() || typeof fetch === 'undefined') {
      this.failSpeech(new Error('浏览器不支持语音朗读'));
      return;
    }
    this.isSpeaking = true;
    this.remotePlayhead = 0;
    this.playRemoteChunks(this.speechSession);
  }

  async playRemoteChunks(session) {
    let next = this.speechQueue.length ? this.openRemoteStream(this.speechQueue.shift()) : null;

    while (next) {
      try {
        const response = await next;
        if (session !== this.speechSession) return;
        // Start synthesizing the next chunk while this one streams and plays.
        next = this.speechQueue.length ? this.openRemoteStream(this.speechQueue.shift()) : null;
        await this.streamPcmResponse(response, session);
      } catch (error) {
        if (session !== this.speechSession) return;
        console.error('Remote speech synthesis error', error);
        showToast(`语音朗读失败：${error.message || '请稍后重试'}`, 'error', 3200);
        this.failSpeech(error);
        return;
      }
      if (session !== this.speechSession) return;
    }

    await this.waitForRemotePlayback(session);
    if (session === this.speechSession) this.finishSpeech();
  }

  /** Read the PCM body as it arrives and schedule each piece right behind the previous one. */
  async streamPcmResponse(response, session) {
    const sampleRate = Number(response.headers.get('X-Audio-Sample-Rate')) || REMOTE_TTS_SAMPLE_RATE;

    if (!response.body || typeof response.body.getReader !== 'function') {
      // No streaming body (old WebView): play the whole chunk once it has downloaded.
      const buffer = await response.arrayBuffer();
      if (session === this.speechSession) this.schedulePcm(new Uint8Array(buffer), sampleRate);
      return;
    }

    const reader = response.body.getReader();
    this.remoteReader = reader;
    let carry = null;
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done || session !== this.speechSession) break;
        if (!value || !value.byteLength) continue;

        let bytes = value;
        if (carry) {
          bytes = new Uint8Array(carry.byteLength + value.byteLength);
          bytes.set(carry, 0);
          bytes.set(value, carry.byteLength);
          carry = null;
        }
        // Network chunks can split a 16-bit sample; keep the odd byte for the next read.
        if (bytes.byteLength % 2) {
          carry = bytes.slice(bytes.byteLength - 1);
          bytes = bytes.subarray(0, bytes.byteLength - 1);
        }
        this.schedulePcm(bytes, sampleRate);
      }
    } finally {
      if (this.remoteReader === reader) this.remoteReader = null;
    }
  }

  schedulePcm(bytes, sampleRate) {
    const context = this.audioContext;
    const sampleCount = Math.floor(bytes.byteLength / 2);
    if (!context || !sampleCount) return;

    const view = new DataView(bytes.buffer, bytes.byteOffset, sampleCount * 2);
    const audioBuffer = context.createBuffer(1, sampleCount, sampleRate);
    const channel = audioBuffer.getChannelData(0);
    for (let i = 0; i < sampleCount; i += 1) {
      channel[i] = view.getInt16(i * 2, true) / 32768;
    }

    const source = context.createBufferSource();
    source.buffer = audioBuffer;
    source.playbackRate.value = this.speechCallbacks.rate || 1;
    source.connect(context.destination);

    // Queue gaplessly after what is already scheduled; after an underrun, restart with a small lead.
    const startAt = Math.max(this.remotePlayhead, context.currentTime + REMOTE_TTS_START_LEAD_S);
    source.start(startAt);
    this.remotePlayhead = startAt + (audioBuffer.duration / source.playbackRate.value);

    this.remoteSources.add(source);
    source.onended = () => {
      this.remoteSources.delete(source);
    };
  }

  /** Resolve once everything scheduled has played (or playback was stopped). */
  waitForRemotePlayback(session) {
    return new Promise((resolve) => {
      const check = () => {
        const context = this.audioContext;
        if (session !== this.speechSession || !context) {
          resolve();
          return;
        }
        const remaining = this.remotePlayhead - context.currentTime;
        if (remaining <= 0.02 && context.state !== 'suspended') {
          resolve();
          return;
        }
        // While paused (suspended) keep waiting; resumeSpeaking() lets the clock run again.
        window.setTimeout(check, Math.min(500, Math.max(50, remaining * 1000)));
      };
      check();
    });
  }

  stopRemoteSpeech() {
    this.remoteControllers.forEach((controller) => controller.abort());
    this.remoteControllers = [];
    if (this.remoteReader) {
      this.remoteReader.cancel().catch(() => {});
      this.remoteReader = null;
    }
    this.remoteSources.forEach((source) => {
      try {
        source.onended = null;
        source.stop();
      } catch (error) {
        // Already stopped.
      }
    });
    this.remoteSources.clear();
    this.remotePlayhead = 0;
    // A paused context would otherwise stay silent for the next run.
    if (this.audioContext && this.audioContext.state === 'suspended' && this.remotePaused) {
      this.audioContext.resume().catch(() => {});
    }
    this.remotePaused = false;
  }

  isRemoteSpeaking() {
    return this.isSpeaking && !this.currentUtterance && !!this.audioContext;
  }

  stopSpeaking() {
    this.isStoppingSpeech = true;
    this.speechSession += 1;
    if (this.synth && (this.synth.speaking || this.synth.pending)) {
      this.synth.cancel();
    }
    this.stopRemoteSpeech();
    this.speechQueue = [];
    this.speechCallbacks = {};
    this.isSpeaking = false;
    this.currentUtterance = null;
    setTimeout(() => {
      this.isStoppingSpeech = false;
    }, 0);
  }

  pauseSpeaking() {
    if (this.isRemoteSpeaking()) {
      // Suspending the context freezes the timeline, so queued pieces keep their order.
      this.remotePaused = true;
      this.audioContext.suspend().catch(() => {});
      return;
    }
    if (this.synth && this.synth.speaking && !this.synth.paused) {
      this.synth.pause();
    }
  }

  resumeSpeaking() {
    if (this.isRemoteSpeaking()) {
      this.remotePaused = false;
      this.audioContext.resume().catch(() => {});
      return;
    }
    if (this.synth && this.synth.paused) {
      this.synth.resume();
    }
  }
}

export const speechService = new SpeechService();

// ---------------------------------------------------------------------------
// Hold-to-talk voice input (WeChat style)
// ---------------------------------------------------------------------------

const HOLD_MAX_RECORDING_MS = 60000;
// Releasing sooner than this counts as a tap: nothing is sent and a toast explains how to use it.
const HOLD_MIN_PRESS_MS = 600;
// Sliding the finger this far up from where the press started arms "release to cancel".
const HOLD_CANCEL_DISTANCE_PX = 80;
const HOLD_COUNTDOWN_SECONDS = 10;
const HOLD_BAR_FACTORS = [0.4, 0.58, 0.76, 0.94, 1.12, 1.3, 1.12, 0.94, 0.76, 0.58, 0.4];
const HOLD_STYLE_ID = 'aihelp-hold-to-talk-style';

const MIC_ICON_PATH = 'M128,176a48.05,48.05,0,0,0,48-48V64a48,48,0,0,0-96,0v64A48.05,48.05,0,0,0,128,176ZM96,64a32,32,0,0,1,64,0v64a32,32,0,0,1-64,0Zm40,143.6V240a8,8,0,0,1-16,0V207.6A80.11,80.11,0,0,1,48,128a8,8,0,0,1,16,0,64,64,0,0,0,128,0,8,8,0,0,1,16,0A80.11,80.11,0,0,1,136,207.6Z';
const CLOSE_ICON_PATH = 'M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z';

// The overlay lives on document.body, outside any component, so its styles are injected once here.
const HOLD_TO_TALK_CSS = `
.aihelp-hold-to-talk {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgba(10, 12, 16, 0.78);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  color: #fff;
  /* The press is tracked through pointer capture on the button, so the mask must not take events. */
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.16s ease, visibility 0s linear 0.16s;
}
.aihelp-hold-to-talk.is-visible {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.16s ease;
}
.aihelp-hold-to-talk .htt-stage {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(24px, env(safe-area-inset-top)) 16px 24px;
}
.aihelp-hold-to-talk .htt-bubble {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  min-width: 168px;
  max-width: min(560px, 100%);
  padding: 16px 20px;
  color: #fff;
  /* No bubble background: the text sits directly on the dark mask, with a soft shadow around its edges. */
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6), 0 0 10px rgba(0, 0, 0, 0.4);
  transform: translateY(-6vh);
  transition: color 0.15s ease;
}
.aihelp-hold-to-talk .htt-transcript {
  display: none;
  align-self: stretch;
  max-height: 38vh;
  overflow-y: auto;
  font-size: 17px;
  line-height: 1.6;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
}
.aihelp-hold-to-talk .htt-bubble.has-text .htt-transcript {
  display: block;
}
.aihelp-hold-to-talk .htt-partial {
  color: #fff;
}
.aihelp-hold-to-talk .htt-bars {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 26px;
}
.aihelp-hold-to-talk .htt-bar {
  width: 3px;
  height: 26px;
  border-radius: 2px;
  background: currentColor;
  opacity: 0.78;
  transform: scaleY(0.2);
  transition: transform 0.09s linear;
}
.aihelp-hold-to-talk .htt-bubble.has-text .htt-bars,
.aihelp-hold-to-talk .htt-bubble.has-text .htt-bar {
  height: 16px;
}
.aihelp-hold-to-talk.recognizing .htt-bars {
  opacity: 0.45;
}
.aihelp-hold-to-talk.cancel-armed .htt-bubble {
  color: #ff6b6b;
}
.aihelp-hold-to-talk.cancel-armed .htt-partial {
  color: #ff6b6b;
}
.aihelp-hold-to-talk .htt-bottom {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.aihelp-hold-to-talk .htt-cancel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 22px;
  transition: opacity 0.15s ease;
}
.aihelp-hold-to-talk .htt-cancel-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: rgba(255, 255, 255, 0.82);
  transition: transform 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}
.aihelp-hold-to-talk .htt-cancel-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
}
.aihelp-hold-to-talk.cancel-armed .htt-cancel-icon {
  transform: scale(1.22);
  background: #fff;
  color: #111;
}
.aihelp-hold-to-talk.cancel-armed .htt-cancel-label {
  color: #fff;
}
.aihelp-hold-to-talk.recognizing .htt-cancel {
  visibility: hidden;
  opacity: 0;
}
.aihelp-hold-to-talk .htt-hint {
  min-height: 20px;
  margin-bottom: 14px;
  padding: 0 16px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.78);
}
.aihelp-hold-to-talk.nearing .htt-hint {
  color: #ffc35a;
}
.aihelp-hold-to-talk.cancel-armed .htt-hint {
  color: #fff;
}
.aihelp-hold-to-talk .htt-arc {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: calc(128px + env(safe-area-inset-bottom));
  padding-top: 34px;
  border-top-left-radius: 50% 96px;
  border-top-right-radius: 50% 96px;
  background: linear-gradient(180deg, #f2f2f2 0%, #d9d9d9 100%);
  color: #191919;
  transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}
.aihelp-hold-to-talk.preparing .htt-arc svg {
  opacity: 0.45;
}
.aihelp-hold-to-talk.recognizing .htt-arc {
  opacity: 0.8;
}
.aihelp-hold-to-talk.cancel-armed .htt-arc {
  background: linear-gradient(180deg, #4a4a4a 0%, #333 100%);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme="dark"] .aihelp-hold-to-talk {
  background: rgba(4, 6, 10, 0.82);
}
[data-theme="dark"] .aihelp-hold-to-talk .htt-arc {
  background: linear-gradient(180deg, #3a3f4b 0%, #2a2e37 100%);
  color: #e6e9ef;
}
[data-theme="dark"] .aihelp-hold-to-talk.cancel-armed .htt-arc {
  background: linear-gradient(180deg, #23262d 0%, #1a1c21 100%);
  color: rgba(230, 233, 239, 0.5);
}
@media (max-width: 430px) {
  .aihelp-hold-to-talk .htt-transcript { font-size: 16px; }
  .aihelp-hold-to-talk .htt-bubble { padding: 14px 16px; }
}
@media (max-height: 520px) {
  .aihelp-hold-to-talk .htt-transcript { max-height: 30vh; }
  .aihelp-hold-to-talk .htt-cancel { margin-bottom: 12px; }
  .aihelp-hold-to-talk .htt-cancel-icon { width: 52px; height: 52px; }
  .aihelp-hold-to-talk .htt-arc {
    height: calc(88px + env(safe-area-inset-bottom));
    padding-top: 22px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .aihelp-hold-to-talk,
  .aihelp-hold-to-talk .htt-bubble,
  .aihelp-hold-to-talk .htt-bar,
  .aihelp-hold-to-talk .htt-cancel-icon,
  .aihelp-hold-to-talk .htt-arc {
    transition: none;
  }
}
`;

const interpolate = (text, params = {}) =>
  String(text).replace(/\{(\w+)\}/g, (match, key) => (key in params ? String(params[key]) : match));

/** Append recognized text, adding a space only between two alphanumeric ends (mixed Chinese/English). */
export const mergeSpeechText = (existingText, recognizedText) => {
  const current = String(existingText || '');
  const next = String(recognizedText || '').trim();
  if (!next) return current;
  if (!current) return next;
  const needsSpace = /[A-Za-z0-9]$/.test(current) && /^[A-Za-z0-9]/.test(next);
  return `${current}${needsSpace ? ' ' : ''}${next}`;
};

/** Map SDK / microphone errors to a message for the user. */
export const describeSpeechError = (error, translate = interpolate) => {
  const message = String((error && error.message) || '');
  if (/^Unauthorized|code 4401/.test(message)) {
    return translate('语音服务鉴权失败，请联系管理员。');
  }
  if (/^Server busy|code 4429/.test(message)) {
    return translate('语音服务繁忙，请稍后再试。');
  }
  if (/^(WebSocket|Connection closed)/.test(message)) {
    return translate('无法连接语音识别服务，请检查网络后重试。');
  }
  if (/^Microphone capture is not available/.test(message)) {
    return translate('当前浏览器不支持实时语音输入，请在 HTTPS 下使用最新版 Chrome、Edge 或 Firefox。');
  }
  return normalizeRecorderError(error).message;
};

const createIcon = (pathData, size) => {
  const svgNs = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNs, 'svg');
  svg.setAttribute('viewBox', '0 0 256 256');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(svgNs, 'path');
  path.setAttribute('d', pathData);
  svg.appendChild(path);
  return svg;
};

const createElement = (className, parent) => {
  const element = document.createElement('div');
  element.className = className;
  if (parent) parent.appendChild(element);
  return element;
};

const setText = (element, value) => {
  const nextValue = value == null ? '' : String(value);
  if (element.textContent !== nextValue) element.textContent = nextValue;
};

/**
 * WeChat-style hold-to-talk.
 *
 * Press and hold the button to record; releasing sends the recognized, punctuated text
 * through onResult. Sliding up before releasing (or pressing Esc) cancels. A tap that is
 * too short shows a toast instead. While the button is held, a full-screen dark mask shows
 * the live transcript.
 *
 *   const voice = new HoldToTalk({ onResult(text) {}, onStateChange(state) {} });
 *   <button @pointerdown="voice.handlePointerDown($event)" @keydown="voice.handleKeyDown($event)">
 *   voice.destroy();   // when the owning component goes away
 *
 * state: idle | preparing (starting mic and connection) | recording | recognizing (waiting for punctuation)
 */
export class HoldToTalk {
  constructor({ onResult, onStateChange, translate } = {}) {
    this.onResult = onResult || (() => {});
    this.onStateChange = onStateChange || (() => {});
    this.translate = translate || interpolate;

    this.state = 'idle';
    this.recognizer = null;
    this.press = null;
    this.cancelArmed = false;
    this.finalText = '';
    this.partialText = '';
    this.level = 0;
    this.tick = 0;
    this.recordingStartedAt = 0;
    this.limitTimer = null;
    this.visualTimer = null;
    this.nodes = null;

    this.handleWindowPointerMove = this.handleWindowPointerMove.bind(this);
    this.handleWindowPointerUp = this.handleWindowPointerUp.bind(this);
    this.handleWindowKeyDown = this.handleWindowKeyDown.bind(this);
    this.handleWindowKeyUp = this.handleWindowKeyUp.bind(this);
    this.handleWindowBlur = this.handleWindowBlur.bind(this);
  }

  t(text, params) {
    return this.translate(text, params);
  }

  // ----- Button events -----

  handlePointerDown(event) {
    if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;

    // Keep focus changes, text selection and emulated mouse events from interfering with the hold.
    event.preventDefault();
    if (!this.canPress()) return;

    const button = event.currentTarget;
    if (button && typeof button.setPointerCapture === 'function') {
      try {
        button.setPointerCapture(event.pointerId);
      } catch (error) {
        // The pointer is already gone; the window listeners still see the release.
      }
    }

    this.beginPress({ source: 'pointer', pointerId: event.pointerId, startY: event.clientY });
  }

  // Space / Enter held on the focused button works like holding it with the mouse.
  handleKeyDown(event) {
    if (event.key !== ' ' && event.key !== 'Enter') return;

    event.preventDefault();
    if (event.repeat || !this.canPress()) return;

    this.beginPress({ source: 'key', key: event.key });
  }

  // ----- Press tracking -----

  canPress() {
    return !this.press && !this.recognizer;
  }

  beginPress(press) {
    this.press = { ...press, startedAt: Date.now() };
    this.cancelArmed = false;
    window.addEventListener('pointermove', this.handleWindowPointerMove);
    window.addEventListener('pointerup', this.handleWindowPointerUp);
    window.addEventListener('pointercancel', this.handleWindowPointerUp);
    window.addEventListener('keydown', this.handleWindowKeyDown, true);
    window.addEventListener('keyup', this.handleWindowKeyUp, true);
    window.addEventListener('blur', this.handleWindowBlur);
    this.start();
  }

  endPress() {
    if (!this.press) return;
    window.removeEventListener('pointermove', this.handleWindowPointerMove);
    window.removeEventListener('pointerup', this.handleWindowPointerUp);
    window.removeEventListener('pointercancel', this.handleWindowPointerUp);
    window.removeEventListener('keydown', this.handleWindowKeyDown, true);
    window.removeEventListener('keyup', this.handleWindowKeyUp, true);
    window.removeEventListener('blur', this.handleWindowBlur);
    this.press = null;
    this.cancelArmed = false;
  }

  /**
   * Letting go ends the recording. A short tap or an armed cancel discards it,
   * otherwise the punctuated result is sent through onResult.
   * interrupted: the release was forced (window lost focus), so it is not treated as a tap.
   */
  release({ cancel = false, interrupted = false } = {}) {
    const press = this.press;
    if (!press) return;

    const heldMs = Date.now() - press.startedAt;
    const cancelArmed = this.cancelArmed;
    this.endPress();

    if (!this.recognizer || this.state === 'recognizing') return;

    if (cancel || cancelArmed) {
      this.abort();
      return;
    }

    if (!interrupted && heldMs < HOLD_MIN_PRESS_MS) {
      this.abort();
      showToast(this.t('按住时间过短，请按住后说话'), 'warning');
      return;
    }

    if (this.state === 'preparing') {
      this.abort();
      showToast(this.t('麦克风还没准备好，请按住稍等片刻再说话'), 'warning');
      return;
    }

    this.finish();
  }

  isPressPointer(event) {
    return !!this.press && this.press.source === 'pointer' && event.pointerId === this.press.pointerId;
  }

  handleWindowPointerMove(event) {
    if (!this.isPressPointer(event)) return;

    const cancelArmed = (this.press.startY - event.clientY) > HOLD_CANCEL_DISTANCE_PX;
    if (cancelArmed !== this.cancelArmed) {
      this.cancelArmed = cancelArmed;
      this.render();
    }
  }

  handleWindowPointerUp(event) {
    if (this.isPressPointer(event)) this.release();
  }

  handleWindowKeyDown(event) {
    if (!this.press) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.release({ cancel: true });
      return;
    }

    if (this.press.source === 'key' && event.key === this.press.key) {
      // Swallow auto-repeat so the held key does not type or click anything.
      event.preventDefault();
    }
  }

  handleWindowKeyUp(event) {
    if (this.press && this.press.source === 'key' && event.key === this.press.key) {
      event.preventDefault();
      this.release();
    }
  }

  handleWindowBlur() {
    // The release can no longer be observed once the window loses focus; treat it as letting go.
    this.release({ interrupted: true });
  }

  // ----- Recognition -----

  async start() {
    this.finalText = '';
    this.partialText = '';
    this.level = 0;
    this.tick = 0;

    const recognizer = new FunASR.Recognizer({
      url: ASR_URL,
      token: ASR_TOKEN,
      onPartial: (text) => {
        if (this.recognizer !== recognizer) return;
        this.partialText = text;
        this.renderTranscript();
      },
      onFinal: (sentence) => {
        if (this.recognizer !== recognizer) return;
        this.finalText = mergeSpeechText(this.finalText, sentence.text);
        this.partialText = '';
        this.renderTranscript();
      },
      onLevel: (level) => {
        if (this.recognizer !== recognizer) return;
        const nextLevel = Math.max(0, Math.min(1, Number(level) || 0));
        this.level = (this.level * 0.58) + (nextLevel * 0.42);
      },
      onError: (error) => {
        // The SDK sets state to "error" before reporting fatal failures; other errors are per-sentence.
        if (recognizer.state === 'error') {
          this.handleStreamFailure(recognizer, error);
        } else {
          console.warn('Voice recognition warning:', error);
        }
      }
    });

    this.recognizer = recognizer;
    this.setState('preparing');
    this.startVisualTimer();

    try {
      await recognizer.start();
    } catch (error) {
      if (this.recognizer !== recognizer) return;
      this.endPress();
      this.reset();
      showToast(describeSpeechError(error, this.translate), 'error', 3200);
      return;
    }

    if (this.recognizer !== recognizer) {
      // Released or cancelled while the microphone was starting. start() does not
      // observe an earlier abort(), so close the capture it has just opened.
      recognizer.abort();
      return;
    }

    this.recordingStartedAt = Date.now();
    this.limitTimer = window.setTimeout(() => this.handleLimitReached(recognizer), HOLD_MAX_RECORDING_MS);
    this.setState('recording');
  }

  async finish() {
    const recognizer = this.recognizer;
    if (!recognizer || this.state !== 'recording') return;

    this.clearLimitTimer();
    this.setState('recognizing');

    // this.recognizer keeps pointing at it so the last punctuated sentence still lands.
    await recognizer.stop();
    if (this.recognizer !== recognizer) return;

    // Fall back to the unpunctuated partial if the final pass did not arrive in time.
    const text = mergeSpeechText(this.finalText, this.partialText).trim();
    this.reset();
    if (text) {
      this.onResult(text);
    } else {
      showToast(this.t('没有识别到语音，请靠近麦克风再试一次'), 'warning');
    }
  }

  handleLimitReached(recognizer) {
    if (this.recognizer !== recognizer || this.state !== 'recording') return;
    this.endPress();
    showToast(this.t('已达到60秒上限，已自动结束录音'), 'warning');
    this.finish();
  }

  // The connection dropped or the server rejected the session while the user was speaking.
  handleStreamFailure(recognizer, error) {
    // While recognizing, stop() already resolves on the close and keeps what arrived.
    if (this.recognizer !== recognizer || this.state === 'recognizing' || this.state === 'idle') return;

    // Keep whatever was already recognized instead of losing it with the connection.
    const text = mergeSpeechText(this.finalText, this.partialText).trim();
    this.endPress();
    this.reset();
    if (text) this.onResult(text);
    showToast(describeSpeechError(error, this.translate), 'error', 3200);
  }

  abort() {
    const recognizer = this.recognizer;
    if (!recognizer) return;
    this.recognizer = null;
    recognizer.abort();
    this.reset();
  }

  reset() {
    this.recognizer = null;
    this.clearLimitTimer();
    if (this.visualTimer) {
      window.clearInterval(this.visualTimer);
      this.visualTimer = null;
    }
    this.finalText = '';
    this.partialText = '';
    this.level = 0;
    this.tick = 0;
    this.recordingStartedAt = 0;
    this.setState('idle');
  }

  clearLimitTimer() {
    if (this.limitTimer) {
      window.clearTimeout(this.limitTimer);
      this.limitTimer = null;
    }
  }

  destroy() {
    this.endPress();
    this.abort();
    if (this.nodes && this.nodes.root.parentNode) {
      this.nodes.root.parentNode.removeChild(this.nodes.root);
    }
    this.nodes = null;
  }

  setState(state) {
    if (this.state === state) return;
    this.state = state;
    this.render();
    this.onStateChange(state);
  }

  // ----- Overlay -----

  remainingSeconds() {
    if (!this.recordingStartedAt) return Math.ceil(HOLD_MAX_RECORDING_MS / 1000);
    const elapsed = Date.now() - this.recordingStartedAt;
    return Math.max(0, Math.ceil((HOLD_MAX_RECORDING_MS - elapsed) / 1000));
  }

  hintText(remaining) {
    if (this.state === 'recognizing') return this.t('正在识别…');
    if (this.cancelArmed) return this.t('松开 取消');
    if (this.state === 'preparing') return this.t('正在启动麦克风…');
    if (remaining <= HOLD_COUNTDOWN_SECONDS) return this.t('还可以说 {seconds} 秒', { seconds: remaining });
    if (this.press && this.press.source === 'key') return this.t('松开 发送，按 Esc 取消');
    return this.t('松开 发送，上滑 取消');
  }

  ensureOverlay() {
    if (this.nodes) return this.nodes;
    if (typeof document === 'undefined') return null;

    if (!document.getElementById(HOLD_STYLE_ID)) {
      const style = document.createElement('style');
      style.id = HOLD_STYLE_ID;
      style.textContent = HOLD_TO_TALK_CSS;
      document.head.appendChild(style);
    }

    // Layout: transcript bubble in the upper middle, cancel target and the
    // "release to send" dome at the bottom, under the user's finger.
    const root = createElement('aihelp-hold-to-talk');
    root.setAttribute('aria-hidden', 'true');

    const stage = createElement('htt-stage', root);
    const bubble = createElement('htt-bubble', stage);
    const transcript = createElement('htt-transcript', bubble);
    transcript.setAttribute('aria-live', 'polite');
    const finalText = document.createElement('span');
    const partialText = document.createElement('span');
    partialText.className = 'htt-partial';
    transcript.appendChild(finalText);
    transcript.appendChild(partialText);

    const barsWrap = createElement('htt-bars', bubble);
    const bars = HOLD_BAR_FACTORS.map(() => {
      const bar = document.createElement('span');
      bar.className = 'htt-bar';
      barsWrap.appendChild(bar);
      return bar;
    });

    const bottom = createElement('htt-bottom', root);
    const cancel = createElement('htt-cancel', bottom);
    createElement('htt-cancel-icon', cancel).appendChild(createIcon(CLOSE_ICON_PATH, 26));
    const cancelLabel = createElement('htt-cancel-label', cancel);
    const hint = createElement('htt-hint', bottom);
    createElement('htt-arc', bottom).appendChild(createIcon(MIC_ICON_PATH, 30));

    document.body.appendChild(root);
    this.nodes = { root, bubble, transcript, finalText, partialText, bars, cancelLabel, hint };
    return this.nodes;
  }

  render() {
    if (!this.nodes && this.state === 'idle') return;
    const nodes = this.ensureOverlay();
    if (!nodes) return;

    const visible = this.state !== 'idle';
    nodes.root.classList.toggle('is-visible', visible);
    nodes.root.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (!visible) return;

    const remaining = this.remainingSeconds();
    nodes.root.classList.toggle('cancel-armed', this.cancelArmed);
    nodes.root.classList.toggle('preparing', this.state === 'preparing');
    nodes.root.classList.toggle('recognizing', this.state === 'recognizing');
    nodes.root.classList.toggle('nearing', this.state === 'recording' && remaining <= HOLD_COUNTDOWN_SECONDS);
    setText(nodes.hint, this.hintText(remaining));
    setText(nodes.cancelLabel, this.t('取消'));
    this.renderTranscript();
    this.renderBars();
  }

  renderTranscript() {
    const nodes = this.nodes;
    if (!nodes) return;
    nodes.bubble.classList.toggle('has-text', !!(this.finalText || this.partialText));
    setText(nodes.finalText, this.finalText);
    setText(nodes.partialText, this.partialText);
    nodes.transcript.scrollTop = nodes.transcript.scrollHeight;
  }

  renderBars() {
    const nodes = this.nodes;
    if (!nodes) return;
    const level = this.state === 'recording' ? this.level : 0.1;
    HOLD_BAR_FACTORS.forEach((factor, index) => {
      const drift = (Math.sin((this.tick / 2.3) + (index * 0.72)) + 1) / 2;
      const scale = Math.min(1, 0.18 + (level * factor * 0.8) + (drift * 0.14));
      nodes.bars[index].style.transform = `scaleY(${scale.toFixed(3)})`;
    });
  }

  startVisualTimer() {
    if (this.visualTimer) window.clearInterval(this.visualTimer);
    this.visualTimer = window.setInterval(() => {
      this.tick += 1;
      if (this.state !== 'recording') this.level *= 0.86;
      // Re-render so the countdown hint and the level bars stay current.
      this.render();
    }, 90);
  }
}
